// Dense mode for every trade-map surface (Asana 1218949343743567): the profile
// Trade History map, the Active Trades Completed map and the share card.
//
// Locations are state or country centroids, so many partners share one exact
// point. Below the threshold each surface runs its own collision pass
// (spreadPartnerDots) and looks as it always has. Above it, collision would walk
// dots into the ocean, so the map switches to ONE dot per location at the true
// projected point, sized by the location's package total, with nothing moved.
//
// Pure: no React, no d3, no DOM. Every rule here is unit-tested.

/** Dense mode turns on when MORE than this many partners with completed packages are plotted */
export const DENSE_MAP_PARTNER_THRESHOLD = 20;

/** Dense dot radius: DENSE_DOT_MIN_R + DENSE_DOT_RANGE * sqrt(total / maxTotal), so 2.5 to 7px */
export const DENSE_DOT_MIN_R = 2.5;
export const DENSE_DOT_RANGE = 4.5;
/** Dense dots may overlap: an opaque fill at 75% and a 1px stroke in the map background colour keep neighbours separable */
export const DENSE_DOT_FILL_OPACITY = 0.75;
export const DENSE_DOT_STROKE = 1;

/** Dense arc width: DENSE_ARC_MIN_W + DENSE_ARC_RANGE * sqrt(count / maxCount), so 1.25 to 3.5px */
export const DENSE_ARC_MIN_W = 1.25;
export const DENSE_ARC_RANGE = 2.25;

/** Names listed in a dense location tooltip, most packages first */
export const DENSE_TOOLTIP_NAMES = 3;

/**
 * The mode switch. Counts partners with completed packages (received + sent > 0)
 * that have a placeable location, on EVERY surface (Eric, 2026-09-28): an
 * in-transit-only partner still renders but never counts, so the profile map and
 * the share card can never disagree about the mode. Keyed off partners, not
 * trades: 200 trades with 5 partners is 5 dots and needs no dense mode.
 */
export const isDenseMap = (completedPartners: number): boolean => completedPartners > DENSE_MAP_PARTNER_THRESHOLD;

/** n over max, held to [0, 1]; 0 when there is no max (a map of live-only locations) */
const ratio = (n: number, max: number): number => (max > 0 ? Math.min(1, Math.max(n, 0) / max) : 0);

export const denseDotRadius = (locationTotal: number, maxLocationTotal: number): number =>
  DENSE_DOT_MIN_R + DENSE_DOT_RANGE * Math.sqrt(ratio(locationTotal, maxLocationTotal));

export const denseArcWidth = (count: number, maxCount: number): number =>
  DENSE_ARC_MIN_W + DENSE_ARC_RANGE * Math.sqrt(ratio(count, maxCount));

/**
 * The region a location key names: `US-CA` is California, `DE` is Germany with
 * no state. Derived from the KEY, never from a member row: regionKey maps an
 * unknown state (`{state: 'XX', country: 'DE'}`) and no state to the same key,
 * and a group labelled from a member could read "XX, Germany".
 */
export const locationRegion = (key: string): { state: string | null; country: string } => {
  const dash = key.indexOf('-');
  if (dash < 0) return { country: key, state: null };
  return { country: key.slice(0, dash), state: key.slice(dash + 1) };
};

/** What groupByLocation reads from one partner row */
export interface LocationReading {
  /** regionKey of the row (or any key that is one-to-one with its point) */
  key: string;
  lon: number;
  lat: number;
  /** Completed packages the owner received from / sent to this partner */
  inbound: number;
  outbound: number;
  liveInbound?: number;
  liveOutbound?: number;
  /** Only where the surface may name partners (the viewer's own panel map). Never on the public surfaces. */
  name?: string;
}

export interface LocationGroup<R> {
  key: string;
  lon: number;
  lat: number;
  rows: R[];
  /** Partners at this location, live-only ones included */
  partners: number;
  inbound: number;
  outbound: number;
  liveInbound: number;
  liveOutbound: number;
  /** inbound + outbound: the completed package total that sizes the dot */
  total: number;
  /** Up to DENSE_TOOLTIP_NAMES names by package count; empty when the rows carry none */
  topNames: string[];
}

/**
 * One group per location key, summed. Sorted by total DESC then key ASC, so
 * a renderer that iterates in order draws the largest dot first and the small
 * ones sit on top. Names are ranked by the row's own count with a STABLE sort
 * over the incoming order (the backend already orders partners by trade count,
 * then last completed, then id), so ties never shuffle between renders.
 */
export const groupByLocation = <R>(rows: R[], read: (row: R) => LocationReading): LocationGroup<R>[] => {
  const groups = new Map<string, LocationGroup<R> & { named: Array<{ name: string; count: number }> }>();
  rows.forEach((row) => {
    const r = read(row);
    let group = groups.get(r.key);
    if (!group) {
      group = { key: r.key, lon: r.lon, lat: r.lat, rows: [], partners: 0, inbound: 0, outbound: 0, liveInbound: 0, liveOutbound: 0, total: 0, topNames: [], named: [] };
      groups.set(r.key, group);
    }
    group.rows.push(row);
    group.partners += 1;
    group.inbound += r.inbound;
    group.outbound += r.outbound;
    group.liveInbound += r.liveInbound ?? 0;
    group.liveOutbound += r.liveOutbound ?? 0;
    group.total += r.inbound + r.outbound;
    if (r.name) group.named.push({ name: r.name, count: r.inbound + r.outbound });
  });

  return Array.from(groups.values())
    .map(({ named, ...group }) => ({
      ...group,
      topNames: [...named]
        .sort((a, b) => b.count - a.count)
        .slice(0, DENSE_TOOLTIP_NAMES)
        .map((n) => n.name),
    }))
    .sort((a, b) => b.total - a.total || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
};

/** The largest completed package total across the groups (0 when none) */
export const maxLocationTotal = <R>(groups: LocationGroup<R>[]): number => groups.reduce((max, g) => Math.max(max, g.total), 0);

/** The largest single-direction count across the groups: the arc width ceiling */
export const maxLocationCount = <R>(groups: LocationGroup<R>[]): number =>
  groups.reduce((max, g) => Math.max(max, g.inbound, g.outbound), 0);
